const CLAIM_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export class CalculatorClaimDateContractError extends Error {
    constructor(path, message) {
        super(`${path}: ${message}`);
        this.name = "CalculatorClaimDateContractError";
        this.path = path;
    }
}

function requireRecord(value, path) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        throw new CalculatorClaimDateContractError(path, "expected an object");
    }
    return value;
}

function requireExactFields(value, fields, path) {
    const object = requireRecord(value, path);
    const expected = [...fields].sort();
    const actual = Object.keys(object).sort();
    if (
        expected.length !== actual.length
        || expected.some((field, index) => field !== actual[index])
    ) {
        throw new CalculatorClaimDateContractError(
            path,
            `expected exactly fields ${expected.join(", ")}`,
        );
    }
    return object;
}

function requireCanonicalDate(value, path) {
    if (typeof value !== "string" || !CLAIM_DATE_PATTERN.test(value)) {
        throw new CalculatorClaimDateContractError(path, "expected canonical YYYY-MM-DD");
    }
    const [year, month, day] = value.split("-").map(Number);
    const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const daysInMonth = [
        0,
        31,
        leapYear ? 29 : 28,
        31,
        30,
        31,
        30,
        31,
        31,
        30,
        31,
        30,
        31,
    ][month] ?? 0;
    if (day < 1 || day > daysInMonth) {
        throw new CalculatorClaimDateContractError(
            path,
            "expected a valid canonical calendar date",
        );
    }
    return value;
}

export function parseCalculatorClaimDateV1(value, path = "claimDate") {
    const tagged = requireRecord(value, path);
    if (tagged.precision === "exact") {
        const exact = requireExactFields(tagged, ["precision", "date"], path);
        return Object.freeze({
            precision: "exact",
            date: requireCanonicalDate(exact.date, `${path}.date`),
        });
    }
    if (tagged.precision === "approximate") {
        const approximate = requireExactFields(tagged, ["precision", "range"], path);
        if (typeof approximate.range !== "string") {
            throw new CalculatorClaimDateContractError(
                `${path}.range`,
                "expected canonical YYYY-MM-DD/YYYY-MM-DD",
            );
        }
        const parts = approximate.range.split("/");
        if (parts.length !== 2) {
            throw new CalculatorClaimDateContractError(
                `${path}.range`,
                "expected canonical YYYY-MM-DD/YYYY-MM-DD",
            );
        }
        const earliest = requireCanonicalDate(parts[0], `${path}.range`);
        const latest = requireCanonicalDate(parts[1], `${path}.range`);
        if (earliest > latest) {
            throw new CalculatorClaimDateContractError(
                `${path}.range`,
                "expected a chronological date range",
            );
        }
        return Object.freeze({
            precision: "approximate",
            range: `${earliest}/${latest}`,
        });
    }
    throw new CalculatorClaimDateContractError(
        `${path}.precision`,
        "expected exact or approximate",
    );
}
